import { useEffect, useState, useRef } from "react";
import LenderCreditYspControls from "./LenderCreditYspControls";
import { fieldReader, fieldWriter } from "../../services/loanApi";
import { LOAN_EDIT_LOCK_FIELD_IDS, isLoanEditLocked } from "../../utils/loanEditLock";
import { getAccessToken } from "../../services/authApi";
import LoadingOverlay from "../shared/LoadingOverlay";
import Toast from "../shared/Toast";
import Modal from "../shared/Modal";
import { error as logAppInsightError } from "../../utils/logger";
import { logError } from "../../utils/logError";

const btnClass = (disabled) =>
    `btn-base btn-save ${disabled ? "cursor-not-allowed opacity-60" : ""}`;

function ActionButtons({ isReadOnly, onSave }) {
    return (
        <div className="flex gap-2">
            <button
                onClick={() => onSave(false)}
                className={btnClass(isReadOnly)}
                disabled={isReadOnly}
            >
                Save &amp; Generate Lock Confirmation
            </button>
        </div>
    );
}

export default function LenderCreditYsp() {
    // ===== IDs =====
    // Change this if your Loan Amount is a different field
    const LOAN_AMOUNT_ID = "2";

    const PREMIUM_PCT_ID = "CX.Premium.PCT"; // readonly
    const PREMIUM_AMT_ID = "CX.Premium.AMT"; // readonly
    const BROKER_YSP_ID = "CX.PREMIUM.BROKER.YSP"; // input + write-back

    const LENDER_CREDIT_PCT_ID = "NEWHUD.X1143"; // write-back
    const LENDER_CREDIT_AMT_ID = "NEWHUD.X1144"; // write-back
    const YSP_TO_BROKER_PCT_ID = "CX.BROKER.YSP.PCT"; // write-back
    const YSP_TO_BROKER_AMT_ID = "CX.BROKER.YSP.AMT"; // write-back
    const CX_LOAN_TYPE = "CX.LOAN.TYPE";

    const FIELD_IDS = [
        LOAN_AMOUNT_ID,
        PREMIUM_PCT_ID,
        PREMIUM_AMT_ID,
        BROKER_YSP_ID,
        LENDER_CREDIT_PCT_ID,
        LENDER_CREDIT_AMT_ID,
        YSP_TO_BROKER_PCT_ID,
        YSP_TO_BROKER_AMT_ID,
        CX_LOAN_TYPE
    ];
    const [loanTypeValue, setLoanTypeValue] = useState('');

    // ===== State (same style as FeeBreakdown) =====
    const [token, setToken] = useState(null);
    const [fieldValues, setFieldValues] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState("Loading...");
    const [loanid, setLoanid] = useState("");
    const [loanNumber, setLoanNumber] = useState("");
    const [initialSubmitDate, setInitialSubmitDate] = useState("");
    // Read-only before submittal too, once disclosures are out (see utils/loanEditLock).
    const [editLocked, setEditLocked] = useState(false);

    const [skipFetch, setSkipFetch] = useState(false);
    const retryRef = useRef(false);

    const [isSaving, setIsSaving] = useState(false);

    // success toast (same as FeeBreakdown)
    const [showToast, setShowToast] = useState(false);
    const [showToastText, setShowToastText] = useState("Saved successfully...");
    const [toastVisible, setToastVisible] = useState(false);
    const [toastAnimation, setToastAnimation] = useState("animate-fade-in-right");

    // warning toast (optional, like FeeBreakdown required validation)
    const [warningToastVisible, setWarningToastVisible] = useState(false);
    const [warningToastMessage, setWarningToastMessage] = useState("");

    // error modal (same as FeeBreakdown)
    const [showErrorToast, setShowErrorToast] = useState(false);
    const [errorTitle, setErrorTitle] = useState("Submission Error");
    const [errorMessage, setErrorMessage] = useState("");

    // ===== Init - same guest pattern =====
    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();

            try {
                window.elli.script.getObject("loan").then(async function (loanObject) {
                    loanObject.all().then(async function (loan) {
                        var loanObject = loan.loan;
                        setLoanid(loanObject.id);
                        setLoanNumber(loanObject.loanNumber);

                        await getToken(false);
                    });
                });
            } catch (error) {
                const errorCode = logError("LCYSP_LOAN_FETCH_ERROR");
                logAppInsightError("Loan Fetch Error", {
                    component: "LenderCreditYsp",
                    problemId: errorCode,
                    errorCode,
                    rawError: error,
                    errorMessage: `${errorCode} - Failed to fetch loan data from guest script.`
                });
                setLoading(false);
            }
        } else {
            console.warn("elli.script.guest is not available.");
            setLoading(false);
        }
    }, []);

    const getToken = async (skipFieldLoad = false) => {
        setLoading(true);
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const tk = await getAccessToken(authCode);
            setToken(tk);
            setSkipFetch(skipFieldLoad);
        } catch (err) {
            const errorCode = logError("LCYSP_TOKEN_FETCH_ERROR");
            logAppInsightError("Token Fetch Error", {
                component: "LenderCreditYsp",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch access token for loan.`
            });
            setLoading(false);
        }
    };

    const isBusinessLoan = (loanTypeValue) =>
        loanTypeValue?.toLowerCase().includes("business");

    const fetchFields = async () => {
        setLoading(true);
        let locked = false;
        try {
            const data = await fieldReader(token, [...FIELD_IDS, ...LOAN_EDIT_LOCK_FIELD_IDS], loanid);

            // const brokerRaw = data[BROKER_YSP_ID];
            // const brokerYspPctRaw = data[YSP_TO_BROKER_PCT_ID];

            // const brokerEmpty = brokerRaw === "" || brokerRaw === null || brokerRaw === undefined;
            // const hasBrokerYspPct = brokerYspPctRaw !== "" && brokerYspPctRaw !== null && brokerYspPctRaw !== undefined;

            // const normalized = (brokerEmpty && hasBrokerYspPct)
            //     ? { ...data, [BROKER_YSP_ID]: brokerYspPctRaw }
            //     : data;
            
            const brokerYspPctRaw = data[YSP_TO_BROKER_PCT_ID];
            // Always set "Broker YSP" to "YSP to Broker" value
            const normalized = { ...data, [BROKER_YSP_ID]: brokerYspPctRaw };

            setFieldValues(normalized);
            setLoanTypeValue(normalized[CX_LOAN_TYPE]);
            locked = isLoanEditLocked(data);
            setEditLocked(locked);
        } catch (err) {
            const errorCode = logError("LCYSP_FIELD_FETCH_ERROR");
            logAppInsightError("Field Fetch Error", {
                component: "LenderCreditYsp",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to fetch field values from Encompass.`
            });
        } finally {
            if ((initialSubmitDate && initialSubmitDate !== "//") || locked) {
                setTimeout(() => makeFormReadOnly(), 100);
            }
            setLoading(false);
        }
    };

    const makeFormReadOnly = () => {
        const selectors = [
            'input:not([type=hidden]):not([readonly]):not([data-force-edit])',
            'textarea:not([readonly]):not([data-force-edit])',
            'select:not([disabled]):not([data-force-edit])',
            'input[type=checkbox]:not([disabled]):not([data-force-edit])'
        ];

        const elements = document.querySelectorAll(selectors.join(","));
        elements.forEach((el) => {
            if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
                el.setAttribute("readOnly", true);
            } else {
                el.setAttribute("disabled", true);
            }
            el.classList.add("cursor-not-allowed");
        });
    };

    useEffect(() => {
        if (!token || !loanid) return;

        if (skipFetch) {
            setSkipFetch(false);
            return;
        }

        fetchFields();
    }, [token, loanid]);

    // success toast animation (same as FeeBreakdown)
    useEffect(() => {
        if (showToast) {
            setToastVisible(true);
            setToastAnimation("animate-fade-in-right");

            const hideTimer = setTimeout(() => {
                setToastAnimation("animate-fade-out-right");

                const vanishTimer = setTimeout(() => {
                    setShowToast(false);
                    setToastVisible(false);
                }, 500);

                return () => clearTimeout(vanishTimer);
            }, 4000);

            return () => clearTimeout(hideTimer);
        }
    }, [showToast]);

    // ===== Helpers =====
    const isReadOnly = (initialSubmitDate && initialSubmitDate !== "//") || editLocked;

    const parseNum = (v) => {
        if (v === null || v === undefined || v === "") return 0;
        const s = String(v).replace(/[$,]/g, "").trim();
        const n = Number(s);
        return isNaN(n) ? 0 : n;
    };

    // ===== Calculations (Excel logic) =====
    const loanAmount = parseNum(fieldValues[LOAN_AMOUNT_ID]);
    const premiumPct = parseNum(fieldValues[PREMIUM_PCT_ID]);
    const brokerYsp = parseNum(fieldValues[BROKER_YSP_ID]);

    // Borrower Credit = Premium % - Broker YSP
    const borrowerCredit = premiumPct - brokerYsp;

    // Lender Credit % = Borrower Credit (per your sheet)
    const lenderCreditPct = borrowerCredit;

    // Lender Credit Amount = LoanAmount * LenderCredit% / 100
    const lenderCreditAmt = (loanAmount * lenderCreditPct) / 100;

    // YSP to Broker % = Broker YSP
    const yspToBrokerPct = brokerYsp;

    // YSP to Broker Amount = LoanAmount * BrokerYSP / 100
    const yspToBrokerAmt = (loanAmount * yspToBrokerPct) / 100;

    // ===== Required validation (Broker YSP must be entered) =====
    const validateRequiredFields = () => {
        const val = fieldValues[BROKER_YSP_ID];
        const has = String(val ?? "").trim() !== "";
        if (!has) return ["Broker YSP"];
        return [];
    };

    // ===== Save (FeeBreakdown style: loader + retry 401 + Modal errors) =====
    const handleSave = async (isRetry = false) => {
        if (brokerYsp > premiumPct) {
            setErrorTitle("Invalid Broker YSP");
            setErrorMessage("Broker YSP cannot exceed " + premiumPct);
            setShowErrorToast(true);
            return;
        }

        setLoadingText("Saving & Generating Lock Confirmation...");
        setLoading(true);
        setIsSaving(true);

        let updatedFields = [];

        try {
            const missing = validateRequiredFields();
            if (missing.length > 0) {
                setWarningToastMessage(
                    `Please fill all required fields:<br/> • ${missing.join("<br/> • ")}`
                );
                setWarningToastVisible(true);
                setTimeout(() => setWarningToastVisible(false), 5000);
                return;
            }

            // write-back only on Save click
            updatedFields = [
                // ✅ Broker YSP input must be written back
                { id: BROKER_YSP_ID, value: String(brokerYsp) },

                // ✅ calculated fields to write back
                { id: LENDER_CREDIT_PCT_ID, value: String(lenderCreditPct) },

                { id: YSP_TO_BROKER_PCT_ID, value: String(yspToBrokerPct) },
                { id: YSP_TO_BROKER_AMT_ID, value: String(yspToBrokerAmt) }
            ];

            await fieldWriter(token, loanid, updatedFields);

            setShowToastText("Saved & Lock Confirmation Generated");
            setShowToast(true);
            setTimeout(() => setShowToast(false), 10000);

            await fetchFields();
            retryRef.current = false;
        } catch (error) {
            const errorCode = logError("LCYSP_FIELD_WRITE_ERROR");
            logAppInsightError("Field Write Error", {
                component: "LenderCreditYsp",
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to write field data to Encompass.`
            });

            // 401 retry once (same behavior)
            if (error.response?.status === 401 && !isRetry) {
                console.warn("401 detected, regenerating token...");
                await getToken(true);
                retryRef.current = true;
                return handleSave(true);
            }

            const json = error.response?.data || {};
            const title =
                json.summary === "Conflict" && json.details?.includes("locked by another user")
                    ? "Loan Locked by Another User"
                    : json.summary === "Bad Request" && Array.isArray(json.errors) && json.errors.length > 0
                        ? "Invalid Fields Detected"
                        : "Submission Error";

            setErrorTitle(title);

            const mainDetail = json.details || "Submission failed.";

            // Map field errors (FeeBreakdown style) but without JSON form name mapping
            let fieldErrText = "";
            if (Array.isArray(json.errors) && json.errors.length > 0) {
                fieldErrText = json.errors
                    .map((err) => {
                        const match = err.summary?.match?.(/changes\[(\d+)\]\.value/);
                        if (match) {
                            const idx = Number(match[1]);
                            const pf = updatedFields[idx];
                            return `${pf?.id}: ${err.details}`;
                        }
                        return `${err.summary}\n${err.details}`;
                    })
                    .join("\n");
            }

            setErrorMessage(`${mainDetail}${fieldErrText ? "\n" + fieldErrText : ""}`);
            setShowErrorToast(true);
        } finally {
            setLoading(false);
            setIsSaving(false);
            setLoadingText("Loading...");
        }
    };

    // Restricted access check
    if (loanTypeValue && !isBusinessLoan(loanTypeValue)) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50 p-6">
                <div className="bg-white shadow-md rounded-lg p-8 max-w-lg text-center border border-gray-200 relative -mt-20">
                    {/* Error Icon */}
                    <div className="flex justify-center mb-4">
                        <svg
                            className="w-12 h-12 text-red-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L4.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    </div>

                    <h2 className="text-xl font-semibold text-gray-800 mb-3">
                        Restricted Access
                    </h2>
                    <p className="text-gray-600">
                        This page is available only for{" "}
                        <span className="font-medium text-[#006aa9]">
                            Business Purpose Loans.
                        </span>
                    </p>
                </div>
            </div>
        );
    }


    return (
        <>
            <LoadingOverlay loading={loading || isSaving} text={loadingText || "Saving..."} />

            <Toast
                visible={toastVisible}
                type="success"
                message={showToastText}
                onClose={() => {
                    setShowToast(false);
                    setToastVisible(false);
                }}
                animationClass={toastAnimation}
            />

            <Toast
                visible={warningToastVisible}
                type="warning"
                message={warningToastMessage}
                onClose={() => setWarningToastVisible(false)}
                animationClass="animate-fade-in-right"
            />

            <Modal
                title={errorTitle}
                message={errorMessage}
                isOpen={showErrorToast}
                onClose={() => setShowErrorToast(false)}
                type="error"
            />

            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex-grow">
                            <LenderCreditYspControls
                                fieldValues={fieldValues}
                                setFieldValues={setFieldValues}
                                loanAmountId={LOAN_AMOUNT_ID}
                                premiumPctId={PREMIUM_PCT_ID}
                                premiumAmtId={PREMIUM_AMT_ID}
                                brokerYspId={BROKER_YSP_ID}
                                computed={{
                                    borrowerCredit,
                                    lenderCreditPct,
                                    lenderCreditAmt,
                                    yspToBrokerPct,
                                    yspToBrokerAmt
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end mt-4">
                        <ActionButtons isReadOnly={isReadOnly} onSave={handleSave} />
                    </div>
                </div>
            </div>
        </>
    );
}
